import React from "react";
import { PropLabel } from "../PropX";
import * as Radio from "../Radio_v2";
import { mediaQueries } from "../style";
import { DisplayOption, mapResourceLocationToDisplayOption } from "./displayOption";
import { ResourceLocation } from "./types";

type Props = {
  /**
   * The name of the radio group. Used as the `name` attribute on the radio inputs.
   */
  name: string;
  /**
   * The value of the currently selected radio button.
   */
  selected: string | null;

  onSelect(slug: string): void;

  /**
   * The location options that appear in the radio group.
   */
  options: ResourceLocation[];

  /**
   * Whether the entire radio group is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether selecting a radio button in this group is required.
   * @default false
   */
  required?: boolean;

  /**
   * A label to display above the group.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * Testing utility function. Called with each radio button's `value` prop.
   * @default undefined
   */
  "data-testid"?: (value: string) => string;
};

const mapResourceLocationsToDisplayOptions = (regions: ResourceLocation[], mobile: boolean) => {
  const active: DisplayOption[] = [];
  const inactive: DisplayOption[] = [];

  for (const resourceLocation of regions) {
    const option = mapResourceLocationToDisplayOption(resourceLocation, mobile);

    if (resourceLocation.resources.length === 0) {
      /**
       * If there are no resources, we want to push the option into a dropdown.
       */
      inactive.push(option);
    } else {
      /**
       * If there are resource, we want to display it.
       */
      active.push(option);
    }
  }

  // TODO sort active regions by resource count and then by proximity.

  return {
    active,
    inactive,
  };
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.name === nextProps.name &&
    prevProps.selected === nextProps.selected &&
    prevProps.options === nextProps.options &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.required === nextProps.required &&
    PropLabel.isEqual(prevProps.label, nextProps.label)
  );
};

export const ResourceLocationRadioGroup: React.FC<Props> = React.memo((props) => {
  const { name, selected, onSelect, options, disabled = false, required = false, label = "Choose a datacenter region" } = props;
  const testId = props["data-testid"];

  const isMobile = mediaQueries.smallMax.useMatchesBreakpoint();

  const displayOptions = React.useMemo(() => mapResourceLocationsToDisplayOptions(options, isMobile), [options, isMobile]);

  const overflow = React.useMemo(() => {
    if (displayOptions.inactive.length === 0) {
      return;
    }

    return <Radio.ButtonWithOverflow placeholder={{ title: "Additional datacenter regions" }} options={displayOptions.inactive} />;
  }, [displayOptions.inactive, selected]);

  return (
    <Radio.Group name={name} label={label} selected={selected} required={required} disabled={disabled} onSelect={onSelect}>
      {displayOptions.active.map((option) => (
        <Radio.Button key={option.value} {...option} data-testid={testId?.(option.value)} />
      ))}
      {overflow}
    </Radio.Group>
  );
}, propsAreEqual);

ResourceLocationRadioGroup.displayName = "ResourceLocationRadioGroup";
